import { ErrorMessage } from '@hookform/error-message'
import { FormProvider } from 'react-hook-form'

import { StarRatingInput } from './StarRatingInput'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '../../Theme'
import { useReviewForm } from '../hooks'
import { REVIEW_LABELS as LABELS } from '../lib'
import { useReviews } from '../state'

import type { ReviewFormProps } from '../types'

const LABEL_CLASS = 'react-css text-text-color font-sec-font text-base'

/**
 * ReviewForm - Presentational component for review submission
 *
 * All form state and logic is handled by useReviewForm hook.
 * This component only renders the UI.
 */
export function ReviewForm({ productSku, productName }: ReviewFormProps) {
  const { neededRating, submitReview } = useReviews()

  const { state, actions, form, validationRules } = useReviewForm({
    productSku,
    neededRating,
    submitReview,
  })

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = form

  return (
    <div className={'react-css rounded bg-white md:mb-10 mb-5'} id={'review-form'}>
      {/* Form */}
      {!state.formSubmitted && (
        <div className={'react-css p-0 md:p-6 flex items-center justify-center'}>
          <FormProvider {...form}>
            <form
              className={
                'react-css rounded bg-cards-bg p-4 md:p-6 block mb-6 mt-6 md:mt-0 shadow-custom-box-shadow w-full xl:w-1/2'
              }
              onSubmit={(e) => void handleSubmit(actions.handleSubmit)(e)}
            >
              {/* Product Name */}
              <div className={'react-css flex flex-col items-start justify-start mb-6 gap-2'}>
                <p className={'react-css text-text-color font-sec-font text-lg leading-4'}>{LABELS.writeReviewFor}</p>
                <span className={'react-css text-text-color font-main-font font-bold text-center text-2xl'}>
                  {productName}
                </span>
              </div>

              {/* Star Rating */}
              <div className={'react-css flex flex-col items-start justify-start mb-7'}>
                <span className={'react-css font-main-font font-bold text-text-color text-lg'}>{LABELS.rateProduct}</span>
                <span className={'react-css flex flex-row items-center justify-start gap-1 text-text-color'}>
                  <StarRatingInput
                    key={state.starsKey}
                    starSize={'h-12 w-12'}
                    onStarSelect={actions.setRating}
                  />
                  {state.starRatingError && (
                    <span role="alert" className={'text-red-500 text-sm'}>{state.starRatingError}</span>
                  )}
                </span>
              </div>

              {/* Form Fields */}
              <div className={'react-css flex flex-col items-start justify-start mb-6 gap-2'}>
                {/* Nickname */}
                <div
                  className={
                    'react-css flex flex-col items-start md:flex-row md:items-center justify-between gap-5 w-full mb-6'
                  }
                >
                  <div className={'react-css flex flex-col items-start justify-start gap-2 w-full'}>
                    <Label htmlFor="nickname" className={LABEL_CLASS}>
                      {LABELS.nickname}
                    </Label>
                    <Input
                      id="nickname"
                      {...register('nickname', validationRules.nickname)}
                      placeholder={LABELS.nicknamePlaceholder}
                      className={'react-css text-text-color font-main-font h-12'}
                    />
                    <ErrorMessage
                      errors={errors}
                      name="nickname"
                      render={({ message }) => <span role="alert" className={'text-red-500 text-sm'}>{message}</span>}
                    />
                  </div>
                </div>

                {/* Summary */}
                <div className={'react-css flex flex-col items-start justify-start gap-2 w-full mb-6'}>
                  <Label htmlFor="summary" className={LABEL_CLASS}>
                    {LABELS.reviewTitle}
                  </Label>
                  <Input
                    id="summary"
                    {...register('summary', validationRules.summary)}
                    placeholder={LABELS.reviewTitlePlaceholder}
                    className={'react-css text-text-color font-main-font h-12'}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="summary"
                    render={({ message }) => <span role="alert" className={'text-red-500 text-sm'}>{message}</span>}
                  />
                </div>

                {/* Text */}
                <div className={'react-css flex flex-col items-start justify-start gap-2 w-full mb-6'}>
                  <Label htmlFor="text" className={LABEL_CLASS}>
                    {LABELS.reviewText}
                  </Label>
                  <Textarea
                    id="text"
                    {...register('text', validationRules.text)}
                    placeholder={LABELS.reviewTextPlaceholder}
                    rows={6}
                    className={'react-css text-text-color font-main-font'}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="text"
                    render={({ message }) => <span role="alert" className={'text-red-500 text-sm'}>{message}</span>}
                  />
                </div>
              </div>

              {/* Submit Error */}
              {state.submitError && (
                <div role="alert" className={'react-css text-red-500 text-sm text-center mb-4'}>
                  {state.submitError}
                </div>
              )}

              {/* Submit Button */}
              <div className={'react-css flex items-center justify-center gap-2 w-full mb-6'}>
                <Button type="submit" variant="expand" size="expand">
                  {LABELS.submitReview}
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      )}

      {/* Success Modal */}
      {state.isModalOpen && <SuccessModal onClose={actions.handleCloseModal} />}
    </div>
  )
}

/**
 * SuccessModal - shadcn Dialog for success message
 */
interface SuccessModalProps {
  onClose: () => void
}

function SuccessModal({ onClose }: SuccessModalProps) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-sec-font">{LABELS.thankYou}</DialogTitle>
          <DialogDescription className="font-sec-font text-lg">
            {LABELS.reviewSubmitted}
          </DialogDescription>
        </DialogHeader>
        <Button onClick={onClose} variant="expand" size="sm" type="button">
          {LABELS.close}
        </Button>
      </DialogContent>
    </Dialog>
  )
}
